import React from 'react';
import { Link } from 'react-router-dom';
import {
  Code, Database, Cloud, Share2, ClipboardCheck,
  Users, Network, Lock, LayoutDashboard, Boxes, HeartHandshake, MoreHorizontal,
  Briefcase, Target, Award, Brain, Layout, BarChart, ArrowRight, ArrowUpRight,
} from 'lucide-react';
import { motion } from 'framer-motion';
import PublicLayout, { PageHero } from '../components/public/PublicLayout';
import { EASE, Stagger, StaggerItem, SectionHeading, Reveal } from '../components/public/motion';

const CORE_SERVICES = [
  {
    icon: Users,
    title: 'ICT Workforce Augmentation',
    desc: 'Pre-vetted professionals embedded in your delivery teams for contract or surge needs.',
  },
  {
    icon: Briefcase,
    title: 'Government Labour Hire',
    desc: 'End-to-end ICT recruitment for Commonwealth and State agencies, aligned with SME procurement goals.',
  },
  {
    icon: Target,
    title: 'Buyer-Sourced Procurement',
    desc: 'Fair-rate labour hire for panels and buyer-sourced models, with transparent oncosts and reporting.',
  },
  {
    icon: Award,
    title: 'Permanent ICT Recruitment',
    desc: 'From junior to principal-level hires, we source top talent for enduring public and private sector roles.',
  },
  {
    icon: ClipboardCheck,
    title: 'Resume Optimisation',
    desc: 'ATS-ready resume and LinkedIn support to help candidates present clearly for government ICT roles.',
  },
  {
    icon: Code,
    title: 'Technical Skills Testing',
    desc: 'Optional tech screening, coding tests and behavioural interviews to validate technical capability.',
  },
  {
    icon: Brain,
    title: 'AI-Powered Job Matching',
    desc: 'In-house tools use AI to match roles with high-fit candidates faster and more accurately.',
  },
  {
    icon: Layout,
    title: 'Web Design & Development',
    desc: 'UI/UX design and front-end development for internal tools, portals, and citizen-facing platforms.',
  },
  {
    icon: BarChart,
    title: 'Workforce Planning Consultancy',
    desc: 'Strategic workforce advice and ICT skill-gap analysis to support long-term resourcing decisions.',
  },
];

const SPECIALISATIONS = [
  {
    icon: Boxes,
    title: 'ERP',
    desc: 'SAP S/4HANA, TechnologyOne, Workday and Oracle/PeopleSoft consultants and architects across finance, HR and operations platforms.',
  },
  {
    icon: HeartHandshake,
    title: 'CRM',
    desc: 'Dynamics 365, Power Platform and Salesforce specialists: PCF component development, CE architecture and DevOps-driven agile delivery.',
  },
  {
    icon: Database,
    title: 'Data and AI',
    desc: 'Data Engineers, Analysts, Architects, ML/MLOps, NLP, AI, BI Developers, Snowflake, Databricks, ETL and Governance specialists.',
  },
  {
    icon: Code,
    title: 'Software Development',
    desc: 'Full-Stack, Front-End, Mobile, .NET, Java, Python, Node.js, Go, Rust, SharePoint, Low-Code and Geospatial specialists.',
  },
  {
    icon: Lock,
    title: 'Cyber',
    desc: 'SOC Analysts, Cyber Engineers, DevSecOps, IRAP Assessors, Identity Management, and Risk/GRC specialists.',
  },
  {
    icon: ClipboardCheck,
    title: 'Program & Project Delivery',
    desc: 'Program Managers, PMs, POs, Scrum Masters, Delivery Leads, Release Train Engineers and Change Managers.',
  },
  {
    icon: LayoutDashboard,
    title: 'Architecture',
    desc: 'Enterprise, Solution, Data, Cloud, Integration, API and Security Architects.',
  },
  {
    icon: Cloud,
    title: 'Cloud Engineering & DevOps',
    desc: 'AWS, Azure, GCP Engineers, SREs, IaC (Terraform, Bicep), CI/CD, Cloud Security, Serverless, and Observability specialists.',
  },
  {
    icon: Share2,
    title: 'Integration and Middleware',
    desc: 'MuleSoft, Oracle Fusion, SOA, API Gateway and Enterprise Integration Developers.',
  },
  {
    icon: Network,
    title: 'Network & Infrastructure',
    desc: 'Network Engineers, Infrastructure Admins, VMware, SCADA, Backup/DR and End-User Compute Specialists.',
  },
  {
    icon: MoreHorizontal,
    title: 'Other',
    desc: 'Technical Business Analysts, Systems Analysts, Test Engineers / Test Analysts, Performance Testers, QA Leads, Service Desk Analysts, Technical Support Engineers, Agile Coaches, Scrum Masters, Technical Writers and Trainers, available on request.',
  },
];

const ServiceCard = ({ icon: Icon, title, desc, dark = false }) => (
  <motion.div
    whileHover={{ y: -6 }}
    transition={{ duration: 0.3, ease: EASE }}
    className={`group relative h-full overflow-hidden rounded-3xl border p-7 transition-all duration-300 ${
      dark
        ? 'border-white/10 bg-white/5 backdrop-blur-sm hover:border-red-500/40 hover:bg-white/10'
        : 'border-gray-200 bg-white hover:shadow-xl hover:shadow-gray-900/5'
    }`}
  >
    <div
      className={`inline-flex rounded-2xl p-3 transition-colors duration-300 ${
        dark
          ? 'bg-red-500/15 text-red-400 group-hover:bg-red-500 group-hover:text-white'
          : 'bg-red-50 text-red-500 group-hover:bg-red-500 group-hover:text-white'
      }`}
    >
      <Icon className="h-6 w-6" />
    </div>
    <h3 className={`mt-5 font-display text-lg font-bold md:text-xl ${dark ? 'text-white' : 'text-gray-900'}`}>
      {title}
    </h3>
    <p className={`mt-2 text-sm md:text-base ${dark ? 'text-gray-400' : 'text-gray-600'}`}>{desc}</p>
  </motion.div>
);

const ServicesPage = () => (
  <PublicLayout>
    <PageHero
      eyebrow="Our services"
      title="Workforce solutions, end to end"
      subtitle="Comprehensive ICT recruitment, labour hire and consultancy, tailored to government and enterprise."
    />

    {/* Specialisation areas */}
    <section className="relative overflow-hidden bg-gray-950 py-20 text-white md:py-28">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -right-32 top-10 h-96 w-96 rounded-full bg-red-500/10 blur-3xl" />
        <div className="absolute -left-24 bottom-10 h-80 w-80 rounded-full bg-red-500/10 blur-3xl" />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 md:px-8">
        <SectionHeading
          dark
          eyebrow="Specialisations"
          title="What we specialise in"
          subtitle="The roles and technology stacks we place every day."
        />
        <Stagger className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3" gap={0.04}>
          {SPECIALISATIONS.map((spec) => (
            <StaggerItem key={spec.title}>
              <ServiceCard {...spec} dark />
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* Core services */}
    <section className="bg-gray-50 py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <SectionHeading
          eyebrow="What we do"
          title="What we offer"
          subtitle="From surge augmentation to permanent placements, every engagement backed by transparent pricing."
        />
        <Stagger className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3" gap={0.05}>
          {CORE_SERVICES.map((service) => (
            <StaggerItem key={service.title}>
              <ServiceCard {...service} />
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* CTA */}
    <section className="bg-white py-20 md:py-24">
      <div className="mx-auto max-w-4xl px-4 text-center md:px-8">
        <SectionHeading
          eyebrow="Next step"
          title="Need talent for your next delivery?"
          subtitle="Tell us about the role and we'll respond with a shortlist of vetted, available specialists."
        />
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-red-500 px-8 py-4 text-lg font-semibold text-white transition-all duration-300 hover:bg-red-600 hover:shadow-xl hover:shadow-red-500/25"
            >
              Get in touch
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/candidates"
              className="group inline-flex items-center justify-center gap-2 rounded-full border-2 border-gray-900 px-8 py-4 text-lg font-semibold text-gray-900 transition-all duration-300 hover:bg-gray-900 hover:text-white"
            >
              View available candidates
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  </PublicLayout>
);

export default ServicesPage;
